// src/shared/streaming/useTelemetryStream.ts
// ────────────────────────────────────────────────────────────────────────────
// React hook for progressive binary telemetry streaming.
//
// Three-phase pipeline:
//   1. CACHE  — instant: reuse already-fetched points for the overlap range
//   2. STREAM — single fetch, BFF streams scope blocks via chunked encoding
//   3. DONE   — all data in, poll timer re-arms with window-scaled interval
//
// UX: BFF flushes each scope block independently → FE decodes and
// renders each device as it arrives — progressive cardiogram fill.
//
// Wire format: binary frames (TelemetryCodec) via fetch ReadableStream.
// Fallback:    JSON fetch when binary endpoint is unavailable.
//
// DRY — the hook is metric-agnostic; callers just specify device IDs
// and the desired time window.  Works for IoT, analytics, or any TS data.
// ────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { TelemetryCache, type TimeRange } from './TelemetryCache';
import { decodeFrames, decodeMuxFrames, type MetricKey, type TimeSeriesPoint } from './TelemetryCodec';
import { ALL_SIGNAL_KEYS, getKeysForNs } from './SignalRegistry';

// ── Public types ─────────────────────────────────────────────────────────

export type StreamPhase = 'idle' | 'cache' | 'streaming' | 'done' | 'empty' | 'error';

export interface TelemetryStreamData {
  /** Per-metric time-series arrays (sorted oldest → newest), merged across all devices. */
  series: Record<MetricKey, TimeSeriesPoint[]>;
  /** Per-device × per-metric breakdown for overlay rendering. */
  byDevice: Record<string, Record<MetricKey, TimeSeriesPoint[]>>;
  /** Current phase of the data pipeline. */
  phase: StreamPhase;
  /** 0–1 fraction of expected data loaded. */
  progress: number;
  /** Human-readable error if phase === 'error'. */
  error: string | null;
  /** Fault domain if phase === 'error': 'network' | 'upstream' | 'database' | 'auth' | 'timeout' | 'bff'. */
  fault: string | null;
  /** True while actively streaming (phases cache or streaming). */
  isLoading: boolean;
  /** True during background refresh poll. */
  isRefreshing: boolean;
}

export interface UseTelemetryStreamOpts {
  /** Device IDs to auto-mix.  Pass empty array to disable. */
  deviceIds: string[];
  /** Visible time window in seconds. */
  windowSec: number;
  /** Row limit hint sent to BFF. */
  limit: number;
  /** BFF base path (default '/bff/orchestrator/hot-stream'). */
  basePath?: string;
  /** Background refresh interval in ms (default 15000). */
  refreshInterval?: number;
  /** Whether streaming is enabled at all (default true). */
  enabled?: boolean;
  /** Scope namespace filter sent to BFF (default 'iot'). */
  scopeNs?: string;
}

// ── Constants ────────────────────────────────────────────────────────────

const ALL_METRICS: MetricKey[] = ALL_SIGNAL_KEYS as MetricKey[];

/** Build empty metric record scoped to a namespace. */
function emptyMetricRecord(scopeNs?: string): Record<MetricKey, TimeSeriesPoint[]> {
  const keys = getKeysForNs(scopeNs) as MetricKey[];
  const r = {} as Record<MetricKey, TimeSeriesPoint[]>;
  for (const k of keys) r[k] = [];
  return r;
}

const EMPTY_SERIES: Record<MetricKey, TimeSeriesPoint[]> = emptyMetricRecord();

// ── RAF throttle for progressive setState ────────────────────────────────
// Coalesces rapid streaming chunks into at most 1 React re-render per frame
// (~16ms at 60fps) to prevent 10–50 unnecessary reconciliation walks.

type RafThrottleCb<T> = (v: T) => void;

function rafThrottled<T>(setter: RafThrottleCb<T>): RafThrottleCb<T> {
  let pending: { value: T } | null = null;
  let rafId = 0;
  const flush = () => {
    if (pending) {
      setter(pending.value);
      pending = null;
    }
    rafId = 0;
  };
  return (value: T) => {
    pending = { value };
    if (!rafId) rafId = requestAnimationFrame(flush);
  };
}

// ── Shared cache instance (survives across re-renders & mounts) ──────────
// maxAgeMs set high (24h) so data is NEVER discarded within a session.
// Once fetched, points stay in-memory for instant reuse across window
// switches.  GC evicts entries only after 24h of no updates.
const globalCache = new TelemetryCache({ maxAgeMs: 24 * 60 * 60 * 1000, maxPoints: 500_000 });
// P1-C: Periodic garbage collection — evict entries older than maxAgeMs.
// Runs every 5 minutes.  maxAgeMs = 24h so data is effectively never
// discarded within a session — once fetched, points stay in-memory for
// instant reuse across window switches (incremental-only buffer).
const GC_INTERVAL_MS = 5 * 60 * 1000;
let gcTimer: ReturnType<typeof setInterval> | null = null;
function ensureCacheGc(): void {
  if (gcTimer) return;
  gcTimer = setInterval(() => globalCache.gc(), GC_INTERVAL_MS);
}

// ── Fetch with deadline ────────────────────────────────────────────────
const FETCH_TIMEOUT_MS = 50_000; // 50s — above BFF’s 45s heavy-read timeout

/** Fetch with an absolute deadline. Aborts if parentSignal fires OR timeout expires. */
function fetchWithTimeout(
  url: string,
  init: RequestInit & { signal?: AbortSignal },
  timeoutMs = FETCH_TIMEOUT_MS,
): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  // Forward parent signal
  if (init.signal) {
    if (init.signal.aborted) { clearTimeout(timer); ctrl.abort(); }
    else init.signal.addEventListener('abort', () => { clearTimeout(timer); ctrl.abort(); }, { once: true });
  }
  return fetch(url, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}

// ── Retry helper ─────────────────────────────────────────────────────
// Single attempt — retries hammer an already-overloaded orchestrator.
// The refresh poll will pick up the data on the next cycle instead.
const MAX_RETRIES    = 0;
const RETRY_BASE_MS  = 1000;
// Quick-retry delay for initial failures (cold orchestrator 502/503).
// Much faster than waiting for the 45-120s refresh poll.
const QUICK_RETRY_DELAY_MS = 2000;
const QUICK_RETRY_MAX      = 2;

// ── Progressive cascade ──────────────────────────────────────────────
// ── Progressive bucket fill ──────────────────────────────────────────
// Right-to-left expanding time slices.  Each bucket is a small, fast
// query — no single request ever covers the entire window, preventing
// Cloud Run timeouts on big windows (1d / 1w got 502 with monolithic).
//
// Growth 2×: each bucket is twice the width of the previous one.
//   bucket₀ = [now-30s, now]           ← most recent, instant
//   bucket₁ = [now-90s, now-30s]       ← gap only
//   bucket₂ = [now-210s, now-90s]      ← doubles again
//   …until the full window is covered.
//
// Cache-aware: buckets whose range is already cached are skipped
// entirely — switching from 1h→1d only fetches the 23h gap.
// Data once fetched is NEVER discarded (incremental-only buffer).

/** Number of IoT metric types the FE consumes (temp, hum, rssi). */
const NUM_METRICS = 3;
/** Assumed sample interval in seconds. */
const SAMPLE_INTERVAL_SEC = 10;

const BUCKET_GROWTH   = 2;
const MIN_BUCKET_SEC  = 30;   // base — overridden by adaptiveMinBucketSec for large windows

/**
 * Adaptive bucket cap — four regimes:
 *
 * Round 16c: 6H stays raw but uses 20min buckets with sparse limit
 * (300/scope).  20min @ 10s interval = 360 rows/scope → 300 returned
 * = 83% coverage per bucket.  18 buckets × CON=6 = 3 groups × ~1s
 * = ~3s total.  LTTB 500 pts/metric preserves full visual fidelity.
 *
 *   <6h:   5 min  → ~74 buckets  (raw, fine-grained recent data)
 *   =6h:  20 min  → ~18 buckets  (raw, sparse limit, CON=6)
 *   ≤1d:   8 hr   → ~6 buckets   (rollup, trivial row count)
 *   >1d:  24 hr   → ~9 buckets   (rollup, trivial row count)
 */
function adaptiveMaxBucketSec(winSec: number): number {
  if (winSec < 21600)   return 300;    // <6h:  5 min  (raw, fine-grained)
  if (winSec === 21600) return 1200;   // =6h: 20 min  (raw, sparse limit)
  if (winSec <= 86400)  return 28800;  // ≤1d:  8 hr   (rollup)
  return 86400;                         // >1d: 24 hr   (rollup)
}

/**
 * Adaptive STARTING bucket size — scales with window.
 *
 * <6h (raw): 30s start → rapid recent-data appearance.
 * =6h (raw sparse): 20min start = max → flat 18 equal buckets.
 * >6h (rollup): large start → rollup queries are instant.
 *
 *   <6h:    30s start → fine-grained recent data
 *   =6h:  1200s (20m) start → flat buckets, no growth phase
 *   ≤1d:  3600s (1h) start → 6 rollup buckets total
 *   >1d: 14400s (4h) start → 9 rollup buckets total
 */
function adaptiveMinBucketSec(winSec: number): number {
  if (winSec < 21600)  return MIN_BUCKET_SEC; // <6h:  30 s
  if (winSec === 21600) return 1200;          // =6h: 20 min (= max, flat)
  if (winSec <= 86400) return 3600;           // ≤1d:  1 hr
  return 14400;                                // >1d:  4 hr
}

/** Adaptive concurrency — shared by initial fill, full-recheck, and
 *  refresh-recover paths so the pipeline stays consistent.
 *
 *  <6h (raw): CON=3 to limit DB pressure on raw LATERAL queries.
 *  ≥6h: CON=6 — sparse limit (300/scope) makes raw queries light;
 *       rollup queries are trivially light. */
function adaptiveConcurrency(winSec: number): number {
  return winSec < 21600 ? 3 : 6;
}

/**
 * Build non-overlapping time buckets (right → left) that cover the full
 * window.  `fromOff` / `toOff` are positive offsets in seconds before `now`.
 *
 * Growth 2× until adaptive cap, then constant-width slices:
 *   ≤6h: cap=5min  → ~74 buckets   (sub-second each)
 *    1d: cap=30min → ~52 buckets   (fast concurrent fill)
 *    1w: cap=1hr   → ~174 buckets  (safe for orchestrator DB)
 *
 * The UX is a smooth progressive fill: recent data appears instantly,
 * historical depth fills in without hammering the BFF.
 */
function buildProgressiveBuckets(winSec: number, minSec?: number): { fromOff: number; toOff: number }[] {
  const maxBucketSec = adaptiveMaxBucketSec(winSec);
  const startSec = minSec ?? MIN_BUCKET_SEC;
  if (winSec <= startSec) {
    return [{ fromOff: winSec, toOff: 0 }];
  }
  const buckets: { fromOff: number; toOff: number }[] = [];
  let cursor   = 0; // seconds before `now` covered so far
  let sliceSec = startSec;
  while (cursor < winSec) {
    const end = Math.min(cursor + sliceSec, winSec);
    buckets.push({ fromOff: end, toOff: cursor });
    cursor   = end;
    sliceSec = Math.min(Math.round(sliceSec * BUCKET_GROWTH), maxBucketSec);
  }
  return buckets;
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useTelemetryStream(opts: UseTelemetryStreamOpts): TelemetryStreamData {
  const {
    deviceIds,
    windowSec,
    limit,
    refreshInterval = 15_000,
    enabled = true,
  } = opts;

  const [series, setSeries]          = useState<Record<MetricKey, TimeSeriesPoint[]>>(EMPTY_SERIES);
  const [byDevice, setByDevice]      = useState<Record<string, Record<MetricKey, TimeSeriesPoint[]>>>({});
  const [phase, setPhase]            = useState<StreamPhase>('idle');
  const [progress, setProgress]      = useState(0);
  const [error, setError]            = useState<string | null>(null);
  const [fault, setFault]            = useState<string | null>(null);
  const [isRefreshing, setRefreshing] = useState(false);

  // RAF-throttled series setters — coalesce rapid streaming chunks to 1 re-render/frame
  const throttledSetSeries   = useRef(rafThrottled(setSeries)).current;
  const throttledSetByDevice = useRef(rafThrottled(setByDevice)).current;
  const throttledSetProgress = useRef(rafThrottled(setProgress)).current;

  // Abort controller ref for cancelling in-flight streams
  const abortRef   = useRef<AbortController | null>(null);
  const timerRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  // True while the initial progressive bucket fill is in progress.
  // Refresh ticks that fire during a fill are deferred — they would race
  // with a shifted time range and cause counts to jump backwards.
  const isFillingRef = useRef(false);
  // Error-aware backoff: consecutive error count → skip refresh cycles
  const consecutiveErrorsRef = useRef(0);
  // Periodic full-window re-check counter.  Every FULL_RECHECK_INTERVAL
  // refresh polls, bypass delta optimization and fetch the entire window.
  // Catches late-arriving data (write-buffer lag, archive recovery) that
  // was missed by the initial cascade.
  const fullRecheckCounter = useRef(0);
  const FULL_RECHECK_INTERVAL = 4;
  // Ref to latest opts for closure safety
  const optsRef    = useRef(opts);
  optsRef.current  = opts;

  // Ensure cache GC is running while this hook is mounted
  useEffect(() => { ensureCacheGc(); }, []);

  // ── Core stream function ─────────────────────────────────────────────

  const streamData = useCallback(async (signal: AbortSignal, isRefresh: boolean) => {
    const { deviceIds: devIds, windowSec: winSec, limit: lim, basePath: bp, scopeNs: ns } = optsRef.current;
    const base = bp || '/bff/orchestrator/hot-stream';
    const metrics = getKeysForNs(ns) as MetricKey[];
    if (!devIds.length) return;

    const now   = Date.now();
    const range: TimeRange = { from: now - winSec * 1000, to: now };

    // Phase 1 — CACHE: instantly show whatever we already have
    if (!isRefresh) {
      setPhase('cache');
      const cached = buildSeriesFromCache(devIds, range, metrics);
      const cachedByDev = buildByDeviceFromCache(devIds, range, metrics);
      setSeries(cached);
      setByDevice(cachedByDev);
      setProgress(estimateProgress(cached, lim, metrics));
      // Yield to React so the cached data renders immediately before
      // the bucket fill loop starts overwriting with progressive updates.
      await new Promise(r => setTimeout(r, 0));
      if (signal.aborted) return;
    } else {
      setRefreshing(true);
    }

    // Phase 2 — STREAM: fetch missing data via binary endpoint
    if (!isRefresh) setPhase('streaming');

    try {
      let fetchOk = false;
      let logMode = 'initial';

      if (!isRefresh && devIds.length > 0) {
        // ── Progressive bucket fill ─────────────────────────────────────
        // Right→left expanding time slices.  Each bucket is a small, fast
        // query.  The cardiogram paints progressively from recent→old.
        // Buckets whose range is already cached are skipped automatically
        // by tryMuxBinaryStream's computeDeltas — no redundant re-fetches.
        const buckets = buildProgressiveBuckets(winSec, adaptiveMinBucketSec(winSec));
        const BUCKET_CONC    = adaptiveConcurrency(winSec);
        const UI_THROTTLE_MS = 1000;  // max 1 render per 1s during fill — smooth
        let lastUiMs = 0;

        isFillingRef.current = true;
        try {
        for (let bi = 0; bi < buckets.length; bi += BUCKET_CONC) {
          if (signal.aborted) return;
          const batch = buckets.slice(bi, bi + BUCKET_CONC);

          const results = await Promise.all(batch.map(bucket => {
            const bucketRange: TimeRange = {
              from: now - bucket.fromOff * 1000,
              to:   now - bucket.toOff * 1000,
            };
            // suppressUiUpdates: inner flushRebuild must not fire setProgress
            // with its heuristic estimateProgress — the outer loop owns progress.
            return tryMuxBinaryStream(devIds, bucketRange, lim, base, signal, { suppressUiUpdates: true });
          }));
          if (results.some(ok => ok)) fetchOk = true;
          if (signal.aborted) return;

          // Throttled progressive UI — at most 1 render / 300ms.
          // Eliminates visual "shift glitch" from 75+ rapid re-renders.
          const batchEnd = Math.min(bi + BUCKET_CONC, buckets.length);
          const isLast = batchEnd >= buckets.length;
          const elapsed = Date.now() - lastUiMs;
          if (elapsed >= UI_THROTTLE_MS || isLast) {
            lastUiMs = Date.now();
            const partial = buildSeriesFromCache(devIds, range, metrics);
            const partialByDev = buildByDeviceFromCache(devIds, range, metrics);
            throttledSetSeries(partial);
            throttledSetByDevice(partialByDev);
            throttledSetProgress(batchEnd / buckets.length);
          }

          if (process.env.NODE_ENV !== 'production') {
            const spanInfo = batch.map(b => `${b.fromOff - b.toOff}s`).join('+');
            const perDev: Record<string, number> = {};
            for (const id of devIds) {
              let c = 0;
              for (const m of metrics) c += globalCache.countInRange(id, m, range);
              perDev[id] = c;
            }
            // eslint-disable-next-line no-console
            console.info(`[telemetry] batch ${Math.floor(bi / BUCKET_CONC) + 1} (buckets ${bi + 1}→${batchEnd}/${buckets.length}, ${spanInfo}) →`, perDev);
          }
        }
        } finally {
          isFillingRef.current = false;
        }
      } else if (isRefresh && devIds.length > 0) {
        // ── Refresh: delta-aware, periodic full-recheck ─────────────────
        const forceFullCheck = ++fullRecheckCounter.current % FULL_RECHECK_INTERVAL === 0;
        if (forceFullCheck) {
          logMode = 'full-recheck';
          for (const id of devIds) {
            for (const m of metrics) {
              globalCache.clearCoverage(id, m);
            }
          }
          // Re-use progressive bucket fill — prevents monolithic queries.
          const recheckConc = adaptiveConcurrency(winSec);
          const rBuckets = buildProgressiveBuckets(winSec, adaptiveMinBucketSec(winSec));
          for (let ri = 0; ri < rBuckets.length; ri += recheckConc) {
            if (signal.aborted) return;
            const rBatch = rBuckets.slice(ri, ri + recheckConc);
            const rResults = await Promise.all(rBatch.map(rb => {
              const bRange: TimeRange = {
                from: now - rb.fromOff * 1000,
                to:   now - rb.toOff * 1000,
              };
              return tryMuxBinaryStream(devIds, bRange, lim, base, signal);
            }));
            if (rResults.some(ok => ok)) fetchOk = true;
          }
        } else {
          // ── Delta-guarded refresh ──────────────────────────────────────
          // Peek at the aggregate delta (uncovered range) before fetching.
          // A normal refresh finds only a ~45 s gap → 1 fast request.
          // But after an aborted fill, tab switch, or coverage eviction the
          // delta can span the entire window (e.g. 24 h), producing a
          // monolithic request that times out at the orchestrator (25 s).
          // Guard: when the delta union exceeds MAX_BUCKET_SEC × 2, fall
          // back to progressive bucket fill for just the uncovered range.
          const allDeltas: TimeRange[] = [];
          for (const id of devIds) {
            for (const d of computeDeltas(id, range, metrics)) allDeltas.push(d);
          }
          if (allDeltas.length === 0) {
            fetchOk = true; // fully cached
          } else {
            let dFrom = Infinity, dTo = -Infinity;
            for (const d of allDeltas) {
              if (d.from < dFrom) dFrom = d.from;
              if (d.to > dTo) dTo = d.to;
            }
            const deltaSpanSec = Math.round((dTo - dFrom) / 1000);
            const maxBucket = adaptiveMaxBucketSec(winSec);

            if (deltaSpanSec > maxBucket * 2) {
              // Wide gap — break into progressive buckets.
              logMode = 'refresh-recover';
              const recoverBuckets = buildProgressiveBuckets(
                deltaSpanSec, adaptiveMinBucketSec(deltaSpanSec),
              );
              const recoverConc = adaptiveConcurrency(winSec);
              const recoverBase = dTo; // delta's right edge = "now" for recovery
              for (let ri = 0; ri < recoverBuckets.length; ri += recoverConc) {
                if (signal.aborted) return;
                const rBatch = recoverBuckets.slice(ri, ri + recoverConc);
                const rRes = await Promise.all(rBatch.map(rb => {
                  const bRange: TimeRange = {
                    from: recoverBase - rb.fromOff * 1000,
                    to:   recoverBase - rb.toOff * 1000,
                  };
                  return tryMuxBinaryStream(devIds, bRange, lim, base, signal);
                }));
                if (rRes.some(ok => ok)) fetchOk = true;
              }
            } else {
              logMode = 'refresh';
              const ok = await tryMuxBinaryStream(devIds, range, lim, base, signal);
              if (ok) fetchOk = true;
            }
          }
        }
      } else {
        fetchOk = true; // no devices — nothing to do
      }

      if (signal.aborted) return;

      // Phase 3 — DONE: read final merged data from cache
      const final = buildSeriesFromCache(devIds, range, metrics);
      const finalByDev = buildByDeviceFromCache(devIds, range, metrics);
      const totalPoints = metrics.reduce((sum, m) => sum + (final[m]?.length ?? 0), 0);

      // Diagnostic: per-device point counts after full-window fetch
      if (process.env.NODE_ENV !== 'production') {
        const perDev: Record<string, string> = {};
        for (const id of devIds) {
          let c = 0;
          let devMin = Infinity, devMax = -Infinity;
          for (const m of metrics) {
            const pts = globalCache.query(id, m, range);
            c += pts.length;
            if (pts.length > 0) {
              devMin = Math.min(devMin, pts[0].ts);
              devMax = Math.max(devMax, pts[pts.length - 1].ts);
            }
          }
          const spanSec = devMin < Infinity ? Math.round((devMax - devMin) / 1000) : 0;
          perDev[id] = `${c}pts/${spanSec}s`;
        }
        // eslint-disable-next-line no-console
        const mode = logMode;
        console.info(
          `[telemetry] full fetch (${winSec}s, ${mode}) →`,
          perDev, `total=${totalPoints}`,
        );
      }

      // If mux failed and cache is empty, surface as error so the UI can
      // show a retry prompt.  When the cache has partial data from a
      // previous fetch, show what we have — the next poll will fill gaps.
      if (!fetchOk && totalPoints === 0 && !isRefresh) {
        const err = new Error('Stream failed') as Error & { fault: string };
        err.fault = 'upstream';
        throw err;
      }

      // P2-B: Skip setState when data hasn't changed — avoids needless
      // downstream memo invalidation on refresh polls / done-phase dupes.
      setSeries(prev => seriesEqual(prev, final, metrics) ? prev : final);
      setByDevice(prev => byDeviceEqual(prev, finalByDev, metrics) ? prev : finalByDev);

      // P2-B: Distinguish "done with data" from "done but empty".
      setPhase(totalPoints > 0 ? 'done' : 'empty');
      setProgress(totalPoints > 0 ? 1 : 0);
      // Clear any lingering error from a previous failed attempt
      setError(null);
      setFault(null);
      setRefreshing(false);
      consecutiveErrorsRef.current = 0;

    } catch (err: any) {
      if (signal.aborted) return;
      consecutiveErrorsRef.current++;
      if (!isRefresh) {
        // Quick retry: on first/second consecutive failure, schedule a
        // retry after 2s instead of waiting for the full refresh interval
        // (45-120s).  Handles cold orchestrator startup (502/503) without
        // requiring the user to manually refresh the page.
        if (consecutiveErrorsRef.current <= QUICK_RETRY_MAX) {
          setPhase('streaming');   // keep loading indicator, not error banner
          setTimeout(() => {
            if (!signal.aborted) streamData(signal, false);
          }, QUICK_RETRY_DELAY_MS);
        } else {
          setPhase('error');
          setFault(err?.fault || null);
          setError(err?.message ?? 'Stream failed');
        }
      }
      setRefreshing(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mux binary stream fetcher ─────────────────────────────────────────

  async function tryMuxBinaryStream(
    devIds: string[], range: TimeRange, lim: number,
    bp: string, signal: AbortSignal,
    opts?: { skipCoverageMark?: boolean; forceFullRange?: boolean; suppressUiUpdates?: boolean },
  ): Promise<boolean> {
    const ns = optsRef.current.scopeNs;
    const metrics = getKeysForNs(ns) as MetricKey[];

    // ── Device + range resolution ───────────────────────────────────────
    // forceFullRange: bypass delta computation — always fetch ALL devices
    // for the ENTIRE range.  Used on initial load and periodic full-recheck
    // to guarantee no data is missed due to stale coverage marks, partial
    // cascade fills, or delta computation edge-cases.
    let neededDevIds: string[];
    let fetchFrom: number;
    let fetchTo: number;

    if (opts?.forceFullRange) {
      neededDevIds = devIds;
      fetchFrom = range.from;
      fetchTo   = range.to;
    } else {
      neededDevIds = devIds.filter(id => computeDeltas(id, range, metrics).length > 0);
      if (neededDevIds.length === 0) return true; // fully cached

      // ── Cursor-based delta range ──────────────────────────────────────
      // Compute the actual needed from/to from the union of missing ranges.
      // On refresh polls this is typically just the right-edge gap (~15 s)
      // instead of the full window — cuts orchestrator DB scans dramatically.
      const allDeltas: TimeRange[] = [];
      for (const id of neededDevIds) {
        const d = computeDeltas(id, range, metrics);
        for (const delta of d) allDeltas.push(delta);
      }
      fetchFrom = allDeltas.length > 0 ? allDeltas[0].from : range.from;
      fetchTo   = allDeltas.length > 0 ? allDeltas[0].to   : range.to;
      for (let i = 1; i < allDeltas.length; i++) {
        if (allDeltas[i].from < fetchFrom) fetchFrom = allDeltas[i].from;
        if (allDeltas[i].to > fetchTo)     fetchTo   = allDeltas[i].to;
      }
    }

    // Single fetch for the full delta range — minimal DB load.
    // BFF streams each scope block progressively via chunked encoding.
    // FE decodes and renders each scope as it arrives — cardiogram fill.
    const params = new URLSearchParams({
      scope_keys:  neededDevIds.join(','),
      scope_ns:    optsRef.current.scopeNs || 'iot',
      limit:       String(lim),
      from:        new Date(fetchFrom).toISOString(),
      to:          new Date(fetchTo).toISOString(),
      format:      'bin',
      view_window: String(optsRef.current.windowSec),  // total viewing context for BFF LTTB tier
    });

    let resp: Response;
    try {
      resp = await fetchWithTimeout(`${bp}/telemetry/mux-bin?${params}`, {
        signal,
        credentials: 'include',
      });
    } catch {
      return false;
    }

    if (!resp.ok || !resp.body) return false;

    // Decode multiplexed binary stream progressively.
    // Micro-batched: merge blocks into cache, defer buildSeriesFromCache
    // rebuilds to a coalescing timer (~30ms) for smooth incremental fill
    // without render thrashing.  30ms ≈ 2 frames @ 60fps — fast enough
    // for analog-like cascade feel, slow enough to batch network chunks.
    const reader = resp.body.getReader();
    let leftover = new Uint8Array(0);
    let dirtyBlocks = 0;
    let rebuildTimer: ReturnType<typeof setTimeout> | null = null;
    // Track which scope keys were actually decoded — only these should be
    // marked as covered on abort.  On normal completion (done=true) all
    // requested devices are marked because the BFF sent everything.
    const decodedScopes = new Set<string>();

    const flushRebuild = () => {
      rebuildTimer = null;
      if (dirtyBlocks === 0) return;
      dirtyBlocks = 0;
      // During bucket-fill the outer loop owns all UI updates — suppress
      // per-stream renders so intermediate fetches don't overwrite the
      // bucket-level progress counter or cause count jumps.
      if (opts?.suppressUiUpdates) return;
      const partial = buildSeriesFromCache(devIds, range, metrics);
      const partialByDev = buildByDeviceFromCache(devIds, range, metrics);
      throttledSetSeries(partial);
      throttledSetByDevice(partialByDev);
      throttledSetProgress(estimateProgress(partial, lim, metrics));
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (signal.aborted) {
        reader.cancel();
        if (rebuildTimer) clearTimeout(rebuildTimer);
        // Only mark coverage for scopes we actually decoded — the rest may
        // have data that was lost when the stream was aborted mid-transfer.
        // Use actual data extent, NOT [fetchFrom, fetchTo] — on abort we
        // only decoded partial data, so marking the full range would hide
        // undelivered scope blocks from future refreshes.
        if (!opts?.skipCoverageMark && decodedScopes.size > 0) {
          for (const devId of neededDevIds) {
            if (!decodedScopes.has(devId)) continue;
            let earliest = Infinity, latest = -Infinity;
            for (const m of metrics) {
              const pts = globalCache.query(devId, m, { from: fetchFrom, to: fetchTo });
              if (pts.length > 0) {
                if (pts[0].ts < earliest) earliest = pts[0].ts;
                if (pts[pts.length - 1].ts > latest) latest = pts[pts.length - 1].ts;
              }
            }
            if (earliest < Infinity && latest > -Infinity) {
              for (const m of metrics) {
                globalCache.markCovered(devId, m, { from: earliest, to: latest });
              }
            }
          }
        }
        return true;
      }

      if (value) {
        const buf = concatBuffers(leftover, value);
        const { blocks, consumed } = decodeMuxFrames(
          buf.buffer as ArrayBuffer, buf.byteOffset, buf.byteLength,
        );

        for (const block of blocks) {
          decodedScopes.add(block.scopeKey);
          for (const frame of block.frames) {
            globalCache.merge(block.scopeKey, frame.metric, frame.points);
          }
        }

        leftover = buf.slice(consumed);

        if (blocks.length > 0) {
          dirtyBlocks += blocks.length;
          if (!rebuildTimer) rebuildTimer = setTimeout(flushRebuild, 30);
        }
      }

      if (done) break;
    }

    if (rebuildTimer) clearTimeout(rebuildTimer);
    if (dirtyBlocks > 0) flushRebuild();

    // Stream completed normally (done=true) — BFF sent all scope blocks.
    //
    // Coverage marking strategy:
    //   We queried [fetchFrom, fetchTo] — the orchestrator returned ALL
    //   rows within that range (LIMIT is coverage-aware).  Any data that
    //   exists in the DB for this range was delivered.
    //
    //   Instead of blindly marking [fetchFrom, fetchTo] as covered, we
    //   check each device's TEMPORAL SPAN.  If the data spans ≥ 50% of
    //   the fetch range, the device genuinely covers it → mark the full
    //   range.  Otherwise, the device has partial history (e.g. recently
    //   started, orchestrator cold-start).  In that case, mark only from
    //   [earliest_actual_point, fetchTo] — the right side is covered but
    //   the empty left gap stays uncovered so future refreshes / periodic
    //   full-checks can discover late-arriving data.
    if (!opts?.skipCoverageMark) {
      const rangeMs = fetchTo - fetchFrom;
      for (const devId of neededDevIds) {
        // Negative coverage: the BFF returned 0 rows for this device.
        // The DB confirmed no data exists in the queried range.
        // Mark it as covered to prevent infinite re-queries of known-empty
        // ranges — the periodic full-recheck (every Nth refresh) clears
        // all coverage to discover late-arriving data.
        // Applied on ALL fetch modes, not just forceFullRange — otherwise
        // a single empty device inflates the delta union range for ALL
        // devices on every refresh poll.
        if (!decodedScopes.has(devId)) {
          for (const m of metrics) {
            globalCache.markCovered(devId, m, { from: fetchFrom, to: fetchTo });
          }
          continue;
        }

        // Find actual data extent for this device across all metrics
        let earliest = Infinity;
        let latest   = -Infinity;
        let devPoints = 0;
        for (const m of metrics) {
          const pts = globalCache.query(devId, m, { from: fetchFrom, to: fetchTo });
          devPoints += pts.length;
          if (pts.length > 0) {
            if (pts[0].ts < earliest) earliest = pts[0].ts;
            if (pts[pts.length - 1].ts > latest) latest = pts[pts.length - 1].ts;
          }
        }

        // Not enough data to judge — skip coverage mark entirely.
        // Next refresh will re-query this device.
        if (devPoints < 6) continue;

        const dataSpanMs = latest - earliest;
        const spanRatio  = rangeMs > 0 ? dataSpanMs / rangeMs : 1;

        if (spanRatio >= 0.5) {
          // Data spans ≥50% of the range → device genuinely covers it.
          // Mark the full query range as covered.
          for (const m of metrics) {
            globalCache.markCovered(devId, m, { from: fetchFrom, to: fetchTo });
          }
        } else {
          // Partial history — only mark from the earliest actual data
          // to the end of the fetch range.  The left gap stays uncovered
          // so periodic full-checks can backfill when data appears.
          for (const m of metrics) {
            globalCache.markCovered(devId, m, { from: earliest, to: fetchTo });
          }
        }
      }
    }

    return true;
  }

  // ── Single-device binary stream (legacy fallback) ────────────────────

  async function tryBinaryStream(
    devIds: string[], range: TimeRange, lim: number,
    bp: string, signal: AbortSignal,
  ): Promise<boolean> {
    const ns = optsRef.current.scopeNs;
    const metrics = getKeysForNs(ns) as MetricKey[];
    // Attempt binary stream for each device
    for (const deviceId of devIds) {
      const deltas = computeDeltas(deviceId, range, metrics);
      if (deltas.length === 0) continue; // fully cached

      for (const delta of deltas) {
        const params = new URLSearchParams({
          scope_key: deviceId,
          scope_ns:  optsRef.current.scopeNs || 'iot',
          limit:     String(lim),
          from:      new Date(delta.from).toISOString(),
          to:        new Date(delta.to).toISOString(),
          format:    'bin',
        });

        let resp: Response;
        try {
          resp = await fetchWithTimeout(`${bp}/telemetry/bin?${params}`, {
            signal,
            credentials: 'include',
          });
        } catch {
          return false; // binary endpoint not available
        }

        if (!resp.ok || !resp.body) return false;

        // Read binary stream progressively
        const reader = resp.body.getReader();
        let leftover = new Uint8Array(0);

        for (;;) {
          const { done, value } = await reader.read();
          if (signal.aborted) { reader.cancel(); return true; }

          if (value) {
            // Concatenate leftover + new chunk
            const chunk = concatBuffers(leftover, value);
            const { frames, consumed } = decodeFrames(chunk.buffer as ArrayBuffer, chunk.byteOffset, chunk.byteLength);

            // Merge decoded frames into cache
            for (const frame of frames) {
              globalCache.merge(deviceId, frame.metric, frame.points);
            }

            // Keep unconsumed bytes for next iteration
            leftover = chunk.slice(consumed);

            // Progressive update — push intermediate state to React (RAF-throttled)
            if (frames.length > 0) {
              const partial = buildSeriesFromCache(devIds, range, metrics);
              const partialByDev = buildByDeviceFromCache(devIds, range, metrics);
              throttledSetSeries(partial);
              throttledSetByDevice(partialByDev);
              throttledSetProgress(estimateProgress(partial, lim, metrics));
            }
          }

          if (done) break;
        }
      }
    }
    return true;
  }

  // ── JSON fallback fetcher ────────────────────────────────────────────

  async function jsonFallback(
    devIds: string[], range: TimeRange, lim: number,
    bp: string, signal: AbortSignal,
  ): Promise<void> {
    const ns = optsRef.current.scopeNs;
    const metrics = getKeysForNs(ns) as MetricKey[];
    // Fetch each device serially to allow progressive cascade rendering
    for (const deviceId of devIds) {
      if (signal.aborted) return;

      const deltas = computeDeltas(deviceId, range, metrics);
      if (deltas.length === 0) continue;

      for (const delta of deltas) {
        if (signal.aborted) return;

        const params = new URLSearchParams({
          scope_key: deviceId,
          scope_ns:  optsRef.current.scopeNs || 'iot',
          limit:     String(lim),
          from:      new Date(delta.from).toISOString(),
          to:        new Date(delta.to).toISOString(),
        });

        const resp = await fetch(`${bp}/iot-telemetry?${params}`, {
          signal,
          credentials: 'include',
        });
        if (!resp.ok) {
          // Parse BFF's structured error to propagate fault domain
          const body = await resp.text().catch(() => '');
          let parsed: { fault?: string; detail?: string; message?: string; error?: string } | null = null;
          try { parsed = JSON.parse(body); } catch { /* not JSON */ }
          const bffErr = new Error(
            parsed?.detail || parsed?.message || `BFF ${resp.status}`,
          ) as Error & { fault: string };
          bffErr.fault = parsed?.fault || 'upstream';
          throw bffErr;
        }

        const data = await resp.json() as {
          temp?: Array<{ ts: number; value: number }>;
          hum?:  Array<{ ts: number; value: number }>;
          rssi?: Array<{ ts: number; value: number }>;
        };

        // Merge into cache
        if (data.temp?.length)  globalCache.merge(deviceId, 'temp',  data.temp);
        if (data.hum?.length)   globalCache.merge(deviceId, 'hum',   data.hum);
        if (data.rssi?.length)  globalCache.merge(deviceId, 'rssi',  data.rssi);

        // Progressive cascade update (RAF-throttled)
        const partial = buildSeriesFromCache(devIds, range, metrics);
        const partialByDev = buildByDeviceFromCache(devIds, range, metrics);
        throttledSetSeries(partial);
        throttledSetByDevice(partialByDev);
        throttledSetProgress(estimateProgress(partial, lim, metrics));
      }
    }
  }

  // ── Effect: trigger stream on param changes ──────────────────────────

  useEffect(() => {
    if (!enabled || !deviceIds.length) {
      setSeries(EMPTY_SERIES);
      setByDevice({});
      setPhase('idle');
      return;
    }

    // ── Immediate abort on window switch ─────────────────────────────
    // Data already decoded into globalCache survives the abort — the new
    // stream's Phase 1 (CACHE) picks it up instantly.  No grace period
    // needed; the old stream's setState calls would race with the new
    // stream's initial cache display.
    if (abortRef.current) abortRef.current.abort();

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Clear stale error from previous window — new fetch sets fresh state
    setError(null);
    setFault(null);
    consecutiveErrorsRef.current = 0;
    fullRecheckCounter.current = 0;

    // Immediately show cache, then stream deltas
    streamData(ctrl.signal, false);

    // Smart refresh interval — scale with window to reduce DB pressure.
    // Tiny windows (≤1m): caller interval (fast) for near-real-time tail.
    // Small windows (≤5m): caller interval (default 8s) for live tail.
    // Medium (≤1h): 10s.  Large (≤6h): 20s.  Huge (>6h): 45s.
    const effectiveRefresh = windowSec <= 300 ? refreshInterval
      : windowSec <= 3600 ? Math.max(refreshInterval, 10_000)
      : windowSec <= 21600 ? Math.max(refreshInterval, 20_000)
      : Math.max(refreshInterval, 45_000);

    if (timerRef.current) clearInterval(timerRef.current);
    let tickCount = 0;
    timerRef.current = setInterval(() => {
      if (ctrl.signal.aborted) return;
      // Don't interrupt the initial bucket fill — it runs on the same
      // signal and concurrent refresh streams would race with a slightly
      // shifted time range, making counts jump backwards and signals
      // appear/disappear.  The fill sets isFillingRef=false when done.
      if (isFillingRef.current) return;
      const errors = consecutiveErrorsRef.current;
      if (errors > 0) {
        const skipCycles = Math.min(8, Math.pow(2, errors - 1));
        tickCount++;
        if (tickCount < skipCycles) return; // skip this cycle
        tickCount = 0;
      }
      streamData(ctrl.signal, true);
    }, effectiveRefresh);

    return () => {
      ctrl.abort();
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [deviceIds.join(','), windowSec, limit, enabled, refreshInterval, streamData]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    series,
    byDevice,
    phase,
    progress,
    error,
    fault,
    isLoading:    phase === 'cache' || phase === 'streaming',
    isRefreshing,
  };
}

// ── Helpers (module-private) ─────────────────────────────────────────────

/** Query cache for all devices and merge metric arrays (namespace-scoped). */
function buildSeriesFromCache(
  deviceIds: string[],
  range: TimeRange,
  metrics: MetricKey[] = ALL_METRICS,
): Record<MetricKey, TimeSeriesPoint[]> {
  const result = emptyMetricRecord(undefined) as Record<MetricKey, TimeSeriesPoint[]>;
  // Only allocate slots for the requested metrics
  for (const m of metrics) result[m] = [];

  for (const devId of deviceIds) {
    for (const m of metrics) {
      const pts = globalCache.query(devId, m, range);
      if (pts.length) result[m] = result[m].concat(pts);
    }
  }

  // Sort each metric oldest → newest
  for (const m of metrics) {
    if (result[m].length > 0) result[m].sort((a, b) => a.ts - b.ts);
  }

  return result;
}

/** Query cache per device for overlay rendering (namespace-scoped). */
function buildByDeviceFromCache(
  deviceIds: string[],
  range: TimeRange,
  metrics: MetricKey[] = ALL_METRICS,
): Record<string, Record<MetricKey, TimeSeriesPoint[]>> {
  const result: Record<string, Record<MetricKey, TimeSeriesPoint[]>> = {};

  for (const devId of deviceIds) {
    const devSeries = {} as Record<MetricKey, TimeSeriesPoint[]>;
    for (const m of metrics) {
      devSeries[m] = globalCache.query(devId, m, range);
    }
    result[devId] = devSeries;
  }

  return result;
}

/** Compute delta ranges still needed for a device (namespace-scoped). */
function computeDeltas(
  deviceId: string,
  range: TimeRange,
  metrics: MetricKey[] = ALL_METRICS,
): TimeRange[] {
  // Take union of missing ranges across scoped metrics
  const allMissing: TimeRange[] = [];
  for (const m of metrics) {
    allMissing.push(...globalCache.missingRanges(deviceId, m, range));
  }
  if (allMissing.length === 0) return [];

  // Merge overlapping ranges
  allMissing.sort((a, b) => a.from - b.from);
  const merged: TimeRange[] = [allMissing[0]];
  for (let i = 1; i < allMissing.length; i++) {
    const last = merged[merged.length - 1];
    if (allMissing[i].from <= last.to) {
      last.to = Math.max(last.to, allMissing[i].to);
    } else {
      merged.push(allMissing[i]);
    }
  }
  return merged;
}

/** Rough progress estimate: points loaded / expected points. */
function estimateProgress(
  series: Record<MetricKey, TimeSeriesPoint[]>,
  expectedLimit: number,
  metrics: MetricKey[] = ALL_METRICS,
): number {
  const total = metrics.reduce((sum, m) => sum + (series[m]?.length ?? 0), 0);
  // expectedLimit covers all metrics combined
  return Math.min(1, total / Math.max(1, expectedLimit));
}

/** Shallow structural equality — true when point counts match per metric.
 *  Used to skip React setState when data hasn't actually changed (e.g.
 *  background refresh with no new points, or done-phase final build
 *  identical to last progressive update). */
function seriesEqual(
  a: Record<MetricKey, TimeSeriesPoint[]>,
  b: Record<MetricKey, TimeSeriesPoint[]>,
  metrics: MetricKey[],
): boolean {
  for (const m of metrics) {
    if ((a[m]?.length ?? 0) !== (b[m]?.length ?? 0)) return false;
  }
  return true;
}

/** Shallow per-device structural equality. */
function byDeviceEqual(
  a: Record<string, Record<MetricKey, TimeSeriesPoint[]>>,
  b: Record<string, Record<MetricKey, TimeSeriesPoint[]>>,
  metrics: MetricKey[],
): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (!b[k]) return false;
    if (!seriesEqual(a[k], b[k], metrics)) return false;
  }
  return true;
}

/** Concatenate two Uint8Arrays. */
function concatBuffers(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length === 0) return b;
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}
